import { m4FromRTS, qFromEulerYXZ, type CreatureDNA } from '../../shared';
import { buildCreature } from '../mesh';
import { lookColors } from '../look';
import { addPose, poseToPalette, postureLayer, previewIdle } from '../rig/pose';
import { Preview2D } from './preview2d';

/** A live miniature of the actual DNA mesh. Also works on phones without WebGPU. */
export function creaturePortrait(initial: CreatureDNA, atelierPortrait?: string): {
  canvas: HTMLCanvasElement; set(dna: CreatureDNA): void; render(ms: number): void; dispose(): void;
} {
  const canvas = document.createElement('canvas');
  canvas.className = 'mc-creature-portrait';
  canvas.setAttribute('aria-hidden', 'true');
  canvas.style.cssText = 'width:100%;height:100%;display:block;touch-action:pan-y';
  const g = canvas.getContext('2d')!;
  let savedImage: HTMLImageElement | null = null;
  if (atelierPortrait?.startsWith('data:image/webp;base64,')) {
    savedImage = new Image(); savedImage.src = atelierPortrait;
  }
  const preview = new Preview2D(g);
  let built = buildCreature(initial, { lod: 1 });
  let colors = lookColors(built.spec, 'constructor');
  let palette = new Float32Array(built.built.rig.bones.length * 16);
  let model = new Float32Array(palette.length);
  const world = new Float32Array(16), q = new Float32Array(4);
  let last = -1000, stopped = false;
  const render = (ms: number): void => {
    if (stopped || ms - last < 80) return;
    last = ms;
    const r = canvas.getBoundingClientRect();
    if (!r.width || !r.height) return;
    const dpr = Math.min(devicePixelRatio || 1, 1.5);
    const w = Math.round(r.width * dpr), h = Math.round(r.height * dpr);
    if (canvas.width !== w || canvas.height !== h) { canvas.width = w; canvas.height = h; }
    if (savedImage?.complete && savedImage.naturalWidth) {
      g.clearRect(0,0,w,h);
      const s = Math.min(w / savedImage.width, h / savedImage.height), iw = savedImage.width * s, ih = savedImage.height * s;
      g.drawImage(savedImage, (w-iw)/2, (h-ih)/2, iw, ih); return;
    }
    const reduced = /^(reduced|min)$/.test(document.documentElement.dataset.motion ?? '');
    const t = reduced ? 1200 : ms;
    g.clearRect(0, 0, w, h);
    const cx = w / 2, cy = h * .84;
    g.fillStyle = '#0c173959'; g.beginPath(); g.ellipse(cx, cy, w * .23, h * .038, 0, 0, Math.PI * 2); g.fill();
    preview.setCamera({ eye: [0, 1.4, 8], target: [0, .56, 0], orthoHalfH: .76, fovYDeg: 20 }, w, h);
    qFromEulerYXZ(q, 0, 16 + Math.sin(t / 2700) * 15, 0);
    const scale = built.spec.v.height;
    m4FromRTS(world, q, [0, 0, 0], [scale, scale, scale]);
    poseToPalette(built.built.rig, addPose(postureLayer(built.spec), previewIdle(built.spec, built.built.rig, t)), world, model, palette);
    preview.lineWidthPx = 1.6;
    preview.draw([{ mesh: built.mesh, rig: built.built.rig, colors, palette }], w, h, dpr);
  };
  return {
    canvas, render,
    set(dna) {
      savedImage = null;
      built = buildCreature(dna, { lod: 1 }); colors = lookColors(built.spec, 'constructor');
      palette = new Float32Array(built.built.rig.bones.length * 16); model = new Float32Array(palette.length); last = -1000;
    },
    dispose() { stopped = true; canvas.width = 1; canvas.height = 1; },
  };
}
